import { IRecord } from '../../../modules/bin/utility';
import {
  AccountClusterCount,
  BodyHashClusterItem,
  DecisionBody,
  DecisionRecord,
  DisagreementReason,
  ErrorEntry,
  EventDetailResponse,
  FanOutResult,
  LockHolder,
  LockState,
  QueueFilters,
  QueueListItem,
} from '../models';

export const CM_QUEUE_FETCH_REQUESTED = 'CM_QUEUE_FETCH_REQUESTED';
export const CM_QUEUE_FETCH_SUCCESS = 'CM_QUEUE_FETCH_SUCCESS';
export const CM_QUEUE_FETCH_FAILURE = 'CM_QUEUE_FETCH_FAILURE';
export const CM_QUEUE_FILTERS_SET = 'CM_QUEUE_FILTERS_SET';
// Append-mode (Load more) — distinct from the replace-mode fetch above so
// reducer logic and isAppending state stay obvious at the call site.
export const CM_QUEUE_APPEND_REQUESTED = 'CM_QUEUE_APPEND_REQUESTED';
export const CM_QUEUE_APPEND_SUCCESS = 'CM_QUEUE_APPEND_SUCCESS';
export const CM_QUEUE_APPEND_FAILURE = 'CM_QUEUE_APPEND_FAILURE';

export const CM_DETAIL_FETCH_REQUESTED = 'CM_DETAIL_FETCH_REQUESTED';
export const CM_DETAIL_FETCH_SUCCESS = 'CM_DETAIL_FETCH_SUCCESS';
export const CM_DETAIL_FETCH_FAILURE = 'CM_DETAIL_FETCH_FAILURE';
export const CM_DETAIL_CLEAR = 'CM_DETAIL_CLEAR';

export const CM_CLUSTER_FETCH_REQUESTED = 'CM_CLUSTER_FETCH_REQUESTED';
export const CM_CLUSTER_FETCH_SUCCESS = 'CM_CLUSTER_FETCH_SUCCESS';
export const CM_CLUSTER_FETCH_FAILURE = 'CM_CLUSTER_FETCH_FAILURE';

export const CM_BODYHASH_FETCH_REQUESTED = 'CM_BODYHASH_FETCH_REQUESTED';
export const CM_BODYHASH_FETCH_SUCCESS = 'CM_BODYHASH_FETCH_SUCCESS';
export const CM_BODYHASH_FETCH_FAILURE = 'CM_BODYHASH_FETCH_FAILURE';

export const CM_LOCK_ACQUIRE_REQUESTED = 'CM_LOCK_ACQUIRE_REQUESTED';
export const CM_LOCK_ACQUIRE_HELD = 'CM_LOCK_ACQUIRE_HELD';
export const CM_LOCK_ACQUIRE_HELD_BY_OTHER = 'CM_LOCK_ACQUIRE_HELD_BY_OTHER';
export const CM_LOCK_ACQUIRE_FAILURE = 'CM_LOCK_ACQUIRE_FAILURE';
export const CM_LOCK_HEARTBEAT_SUCCESS = 'CM_LOCK_HEARTBEAT_SUCCESS';
export const CM_LOCK_HEARTBEAT_FAILURE = 'CM_LOCK_HEARTBEAT_FAILURE';
export const CM_LOCK_RELEASE = 'CM_LOCK_RELEASE';

export const CM_DECISION_SUBMIT_REQUESTED = 'CM_DECISION_SUBMIT_REQUESTED';
export const CM_DECISION_SUBMIT_SUCCESS = 'CM_DECISION_SUBMIT_SUCCESS';
export const CM_DECISION_SUBMIT_FAILURE = 'CM_DECISION_SUBMIT_FAILURE';
export const CM_DECISION_RESET_REQUESTED = 'CM_DECISION_RESET_REQUESTED';
export const CM_DECISION_RESET_SUCCESS = 'CM_DECISION_RESET_SUCCESS';
export const CM_DECISION_RESET_FAILURE = 'CM_DECISION_RESET_FAILURE';

export const CM_FANOUT_SELECT_SET = 'CM_FANOUT_SELECT_SET';
export const CM_FANOUT_SELECT_TOGGLE = 'CM_FANOUT_SELECT_TOGGLE';
export const CM_FANOUT_START = 'CM_FANOUT_START';
export const CM_FANOUT_EVENT_RESULT = 'CM_FANOUT_EVENT_RESULT';
export const CM_FANOUT_FINISH = 'CM_FANOUT_FINISH';
export const CM_FANOUT_RESET = 'CM_FANOUT_RESET';

export const CM_MODAL_OPEN = 'CM_MODAL_OPEN';
export const CM_MODAL_CLOSE = 'CM_MODAL_CLOSE';
export const CM_KEYBOARD_SET = 'CM_KEYBOARD_SET';

export type ContentModerationModal = 'none' | 'block' | 'approveReason' | 'reset';

export interface QueueSlice {
  items: QueueListItem[];
  filters: QueueFilters;
  // Server-echoed paging metadata from the last successful fetch. Drives the
  // "Showing X of N" header — and, later, paginator chrome.
  totalCount: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  isPolling: boolean;
  // True while a Load-more request is in flight (distinct from isLoading so
  // the rest of the list keeps rendering and only the button shows a spinner).
  isAppending: boolean;
  error: ErrorEntry | null;
  lastFetchedAt: number | null;
}

export interface LockSlice {
  state: LockState;
  holder: LockHolder | null;
  lastHeartbeatAt: string | null;
  heartbeatFailures: number;
  error: ErrorEntry | null;
}

export interface FanOutSlice {
  selectedEventIds: number[];
  isRunning: boolean;
  results: Record<number, FanOutResult>;
}

export interface UiSlice {
  modal: ContentModerationModal;
  keyboardEnabled: boolean;
}

export interface ContentModerationState {
  queue: QueueSlice;
  detail: IRecord;
  cluster: IRecord;
  bodyHashCluster: IRecord;
  decisionSubmit: IRecord;
  decisionReset: IRecord;
  lock: LockSlice;
  fanOut: FanOutSlice;
  ui: UiSlice;
}

export type Action =
  | { type: typeof CM_QUEUE_FETCH_REQUESTED; isPoll: boolean }
  | {
      type: typeof CM_QUEUE_FETCH_SUCCESS;
      items: QueueListItem[];
      totalCount: number;
      page: number;
      pageSize: number;
      at: number;
    }
  | { type: typeof CM_QUEUE_FETCH_FAILURE; error: ErrorEntry }
  | { type: typeof CM_QUEUE_FILTERS_SET; filters: QueueFilters }
  | { type: typeof CM_QUEUE_APPEND_REQUESTED }
  | {
      type: typeof CM_QUEUE_APPEND_SUCCESS;
      items: QueueListItem[];
      totalCount: number;
      page: number;
      pageSize: number;
    }
  | { type: typeof CM_QUEUE_APPEND_FAILURE; error: ErrorEntry }
  | { type: typeof CM_DETAIL_FETCH_REQUESTED }
  | { type: typeof CM_DETAIL_FETCH_SUCCESS; detail: EventDetailResponse }
  | { type: typeof CM_DETAIL_FETCH_FAILURE; error: ErrorEntry }
  | { type: typeof CM_DETAIL_CLEAR }
  | { type: typeof CM_CLUSTER_FETCH_REQUESTED; accountId: number }
  | { type: typeof CM_CLUSTER_FETCH_SUCCESS; cluster: AccountClusterCount }
  | { type: typeof CM_CLUSTER_FETCH_FAILURE; error: ErrorEntry }
  | { type: typeof CM_BODYHASH_FETCH_REQUESTED; eventId: number }
  | { type: typeof CM_BODYHASH_FETCH_SUCCESS; items: BodyHashClusterItem[] }
  | { type: typeof CM_BODYHASH_FETCH_FAILURE; error: ErrorEntry }
  | { type: typeof CM_LOCK_ACQUIRE_REQUESTED }
  | { type: typeof CM_LOCK_ACQUIRE_HELD; holder: LockHolder | null; lastHeartbeatAt: string | null }
  | { type: typeof CM_LOCK_ACQUIRE_HELD_BY_OTHER; holder: LockHolder | null; lastHeartbeatAt: string | null }
  | { type: typeof CM_LOCK_ACQUIRE_FAILURE; error: ErrorEntry }
  | { type: typeof CM_LOCK_HEARTBEAT_SUCCESS; lastHeartbeatAt: string }
  | { type: typeof CM_LOCK_HEARTBEAT_FAILURE; error: ErrorEntry }
  | { type: typeof CM_LOCK_RELEASE }
  | { type: typeof CM_DECISION_SUBMIT_REQUESTED; body: DecisionBody }
  | { type: typeof CM_DECISION_SUBMIT_SUCCESS; decision: DecisionRecord; warnings: ErrorEntry[] }
  | { type: typeof CM_DECISION_SUBMIT_FAILURE; error: ErrorEntry }
  | { type: typeof CM_DECISION_RESET_REQUESTED }
  | { type: typeof CM_DECISION_RESET_SUCCESS }
  | { type: typeof CM_DECISION_RESET_FAILURE; error: ErrorEntry }
  | { type: typeof CM_FANOUT_SELECT_SET; eventIds: number[] }
  | { type: typeof CM_FANOUT_SELECT_TOGGLE; eventId: number }
  | { type: typeof CM_FANOUT_START; eventIds: number[] }
  | { type: typeof CM_FANOUT_EVENT_RESULT; result: FanOutResult }
  | { type: typeof CM_FANOUT_FINISH }
  | { type: typeof CM_FANOUT_RESET }
  | { type: typeof CM_MODAL_OPEN; modal: ContentModerationModal }
  | { type: typeof CM_MODAL_CLOSE }
  | { type: typeof CM_KEYBOARD_SET; enabled: boolean };

export type ActionType = Action['type'];

export const CONTENT_MODERATION_SLICE_KEY = 'contentModeration' as const;
